
import { useState, useEffect } from "react";
import { CURRENCY_FORMATS, DEFAULT_SETTINGS, DEFAULT_BRAND } from "./utils.js";
import { 
  updateSettings,
  getCurrentSettings,
  clearCustomFieldFromAllProducts,
  deleteAllInvoices,
  deleteAllProducts,
  deleteAllCustomers,
  getDeletionBatches,
  getProductDeletionBatches,
  getCustomerDeletionBatches,
  restoreInvoiceBatch,
  restoreProductBatch,
  restoreCustomerBatch,
  updateBrandInfo,
  getCurrentBrandInfo,
  FEATURES
} from "./db/index.js";

// ══════════════════════════════════════════════════════════════════════════════
// SESSION STORAGE OPERATIONS (FOR FREE TIER)
// ══════════════════════════════════════════════════════════════════════════════

const SETTINGS_STORAGE_KEY = "invoice_gen_settings_session";
const BRAND_STORAGE_KEY = "invoice_gen_brand_session";

/**
 * Get settings from sessionStorage (FREE tier only)
 * @returns {object} Settings object
 */
const getSettingsFromSession = () => {
  try {
    const data = sessionStorage.getItem(SETTINGS_STORAGE_KEY);
    return data ? JSON.parse(data) : DEFAULT_SETTINGS;
  } catch (error) {
    console.error("Failed to load settings from sessionStorage:", error);
    return DEFAULT_SETTINGS;
  }
};

/**
 * Save settings to sessionStorage (FREE tier only)
 * @param {object} settings - Settings object to save
 */
const saveSettingsToSession = (settings) => {
  try {
    sessionStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error("Failed to save settings to sessionStorage:", error);
    throw error;
  }
};

/**
 * Save brand to sessionStorage (FREE tier only)
 * @param {object} brand - Brand object to save
 */
const saveBrandToSession = (brand) => {
  try {
    sessionStorage.setItem(BRAND_STORAGE_KEY, JSON.stringify(brand));
  } catch (error) {
    console.error("Failed to save brand to sessionStorage:", error);
    throw error;
  }
};

// ══════════════════════════════════════════════════════════════════════════════
// TOAST NOTIFICATION COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const colors = {
    success: { bg: "#DCFCE7", border: "#22C55E", text: "#166534" },
    error: { bg: "#FEE2E2", border: "#EF4444", text: "#991B1B" },
    info: { bg: "#DBEAFE", border: "#3B82F6", text: "#1E40AF" }
  };

  const style = colors[type] || colors.info;

  return (
    <div style={{
      position: "fixed",
      top: 20,
      right: 20,
      background: style.bg,
      border: `2px solid ${style.border}`,
      borderRadius: 10,
      padding: "14px 20px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
      zIndex: 99999,
      maxWidth: 400,
      animation: "slideIn 0.3s ease"
    }}>
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10
      }}>
        <span style={{ fontSize: 18 }}>
          {type === "success" && "✓"}
          {type === "error" && "✕"}
          {type === "info" && "ℹ"}
        </span>
        <span style={{ 
          fontSize: 14, 
          fontWeight: 600, 
          color: style.text,
          fontFamily: "'Sora', sans-serif"
        }}>
          {message}
        </span>
        <button
          onClick={onClose}
          style={{
            marginLeft: "auto",
            background: "transparent",
            border: "none",
            color: style.text,
            fontSize: 18,
            cursor: "pointer",
            padding: 0,
            width: 20,
            height: 20
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// BATCH RESTORATION MODAL COMPONENT (REUSABLE)
// ══════════════════════════════════════════════════════════════════════════════

const BatchRestorationModal = ({ G, entityType, batches, loading, onRestore, onClose }) => {
  const entityConfig = {
    invoice: {
      title: "📦 Deleted Invoice Batches",
      description: "Restore invoices within 30 days of deletion",
      emptyMessage: "No deleted invoice batches found",
      countKey: "invoiceCount",
      countLabel: "invoices"
    },
    product: {
      title: "📦 Deleted Product Batches",
      description: "Restore products within 30 days of deletion",
      emptyMessage: "No deleted product batches found",
      countKey: "productCount",
      countLabel: "products"
    },
    customer: {
      title: "📦 Deleted Customer Batches",
      description: "Restore customers within 30 days of deletion",
      emptyMessage: "No deleted customer batches found",
      countKey: "customerCount",
      countLabel: "customers"
    }
  };

  const config = entityConfig[entityType];

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 9999
    }}>
      <div style={{
        background: G.bg, borderRadius: 12, padding: "28px",
        maxWidth: 700, width: "90%", maxHeight: "80vh", overflow: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: G.ink }}>
          {config.title}
        </div>
        <div style={{ fontSize: 13, color: G.muted, marginBottom: 20, lineHeight: 1.5 }}>
          {config.description}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: G.muted }}>
            Loading batches...
          </div>
        ) : batches.length === 0 ? (
          <div style={{ 
            textAlign: "center", 
            padding: "40px 20px", 
            border: `2px dashed ${G.border}`, 
            borderRadius: 8, 
            color: G.muted 
          }}>
            {config.emptyMessage}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {batches.map((batch, idx) => (
              <div key={batch.batchId} style={{
                background: G.surface,
                border: `1px solid ${G.border}`,
                borderRadius: 8,
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: G.ink, marginBottom: 4 }}>
                    Batch #{idx + 1} — {batch[config.countKey]} {config.countLabel}
                  </div>
                  <div style={{ fontSize: 12, color: G.muted }}>
                    Deleted: {new Date(batch.deletedAt).toLocaleDateString()} • 
                    Expires: {new Date(batch.deletionScheduledAt).toLocaleDateString()}
                    {batch.daysRemaining !== null && batch.daysRemaining !== undefined && (
                      <span> ({batch.daysRemaining} days left)</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onRestore(batch.batchId)}
                  disabled={batch.isExpired || batch.isRestored}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: 7,
                    background: batch.isExpired || batch.isRestored ? G.border : "#22C55E",
                    color: "#FFFFFF",
                    cursor: batch.isExpired || batch.isRestored ? "not-allowed" : "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    opacity: batch.isExpired || batch.isRestored ? 0.5 : 1
                  }}
                >
                  {batch.isRestored ? "Restored" : batch.isExpired ? "Expired" : "Restore"}
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: "100%",
            marginTop: 20,
            padding: "10px 16px",
            border: `1px solid ${G.border}`,
            borderRadius: 7,
            background: G.surface2,
            color: G.ink,
            cursor: "pointer",
            fontFamily: "'Sora', sans-serif",
            fontSize: 13,
            fontWeight: 500
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════

const ModernToggle = ({ G, label, value, onChange, description, disabled = false }) => (
  <div style={{ 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center",
    padding: "12px 0",
    borderBottom: `1px solid ${G.border}15`,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? "not-allowed" : "default"
  }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: G.ink, marginBottom: description ? 4 : 0 }}>
        {label}
      </div>
      {description && (
        <div style={{ fontSize: 11, color: G.muted, lineHeight: 1.4 }}>
          {description}
        </div>
      )}
    </div>
    <div
      onClick={() => !disabled && onChange(!value)}
      style={{
        width: 48,
        height: 26,
        background: disabled ? G.border : (value ? G.accent : G.border),
        borderRadius: 13,
        position: "relative",
        transition: "background .2s",
        cursor: disabled ? "not-allowed" : "pointer",
        flexShrink: 0,
        marginLeft: 16
      }}
    >
      <div style={{
        width: 22,
        height: 22,
        background: "#FFFFFF",
        borderRadius: "50%",
        position: "absolute",
        top: 2,
        left: value ? 24 : 2,
        transition: "left .2s",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
      }} />
    </div>
  </div>
);

const SettingCard = ({ G, children, style = {} }) => (
  <div style={{
    background: G.surface,
    border: `1px solid ${G.border}`,
    borderRadius: 10,
    padding: "20px 24px",
    marginBottom: 16,
    ...style
  }}>
    {children}
  </div>
);

const SectionHeader = ({ G, icon, title, description }) => (
  <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `2px solid ${G.border}` }}>
    <div style={{ 
      fontSize: 15, 
      fontWeight: 600, 
      color: G.ink, 
      marginBottom: 4,
      display: "flex",
      alignItems: "center",
      gap: 8
    }}>
      {icon && <span style={{ fontSize: 18 }}>{icon}</span>}
      {title}
    </div>
    {description && (
      <div style={{ fontSize: 12, color: G.muted, lineHeight: 1.5 }}>
        {description}
      </div>
    )}
  </div>
);

const ButtonGroup = ({ G, options, value, onChange, columns = null, disabled = false }) => (
  <div style={{ 
    display: "grid", 
    gridTemplateColumns: columns || `repeat(${options.length}, 1fr)`,
    gap: 8,
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? "none" : "auto"
  }}>
    {options.map(([val, label]) => (
      <button
        key={val}
        onClick={() => !disabled && onChange(val)}
        disabled={disabled}
        style={{
          padding: "10px 12px",
          border: `1.5px solid ${value === val ? G.accent : G.border}`,
          borderRadius: 7,
          background: value === val ? G.accentBg : G.surface,
          color: value === val ? G.accent : G.ink2,
          fontSize: 12,
          fontWeight: value === val ? 600 : 400,
          cursor: disabled ? "not-allowed" : "pointer",
          fontFamily: "'Sora', sans-serif",
          transition: "all .15s",
          textAlign: "center"
        }}
      >
        {label}
      </button>
    ))}
  </div>
);

const PreviewBox = ({ G, label, children, variant = "default" }) => {
  const variants = {
    default: { bg: G.surface2, border: G.border },
    accent: { bg: G.accentBg, border: `${G.accent}44` },
    success: { bg: "#F0FDF4", border: "rgba(34, 197, 94, 0.2)" }
  };
  const style = variants[variant];

  return (
    <div style={{
      marginTop: 14,
      padding: 12,
      background: style.bg,
      borderRadius: 8,
      border: `1px solid ${style.border}`
    }}>
      <div style={{ fontSize: 11, color: G.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontWeight: 700, color: variant === "accent" ? G.accent : G.ink, fontSize: 16 }}>
        {children}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

export default function Settings({ G, settings, setSettings, brand, setBrand, darkMode, setDarkMode, user }) {
  const [local, setLocal] = useState(settings);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Deletion batch restoration modals (3 separate modals)
  const [showInvoiceBatchModal, setShowInvoiceBatchModal] = useState(false);
  const [showProductBatchModal, setShowProductBatchModal] = useState(false);
  const [showCustomerBatchModal, setShowCustomerBatchModal] = useState(false);

  // Batch data (3 separate arrays)
  const [invoiceBatches, setInvoiceBatches] = useState([]);
  const [productBatches, setProductBatches] = useState([]);
  const [customerBatches, setCustomerBatches] = useState([]);

  // Loading states
  const [loadingInvoiceBatches, setLoadingInvoiceBatches] = useState(false);
  const [loadingProductBatches, setLoadingProductBatches] = useState(false);
  const [loadingCustomerBatches, setLoadingCustomerBatches] = useState(false);

  // Check if user is FREE tier
  const isFreeTier = user?.tierLevel === 0;

  // ══════════════════════════════════════════════════════════════════════════════
  // LOAD FRESH DATA ON MOUNT - TIER-BASED
  // ══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const loadFreshSettings = async () => {
      try {
        if (isFreeTier) {
          // ═══════════════════════════════════════════════════════════════════
          // FREE TIER - Load from sessionStorage
          // ═══════════════════════════════════════════════════════════════════
          
          const sessionSettings = getSettingsFromSession();
          setLocal(sessionSettings);
          
        } else {
          // ═══════════════════════════════════════════════════════════════════
          // PAID TIER - Load from database
          // ═══════════════════════════════════════════════════════════════════
          
          const freshSettings = await getCurrentSettings();
          
          if (freshSettings) {
            setLocal(freshSettings);
          }
        }
            } catch (error) {
        console.error("Failed to load fresh settings:", error);
        
        // Show error to user
        setToast({ 
          message: "Failed to load latest settings. Showing cached data.", 
          type: "error" 
        });
        
        // Keep existing local state (already initialized with prop)
      }
    };
    
    loadFreshSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFreeTier]); // Re-run if tier changes

  // Track if settings have changed
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const changed = JSON.stringify(local) !== JSON.stringify(settings);
      setHasChanges(changed);
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [local, settings]);

  const addSection = () => {
    setLocal(p => ({ ...p, notesSections: [...(p.notesSections || []), { title: "", body: "" }] }));
  };

  const updateSection = (i, field, val) => 
    setLocal(p => ({ ...p, notesSections: p.notesSections.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));
  
  const removeSection = (i) => 
    setLocal(p => ({ ...p, notesSections: p.notesSections.filter((_, idx) => idx !== i) }));

  // ══════════════════════════════════════════════════════════════════════════════
  // SAVE HANDLER (UPDATE SETTINGS) - TIER-BASED
  // ══════════════════════════════════════════════════════════════════════════════

  const handleSave = async () => {
    setSaving(true);
    
    try {
      if (isFreeTier) {
        // ═══════════════════════════════════════════════════════════════════
        // FREE TIER - Save to sessionStorage
        // ═══════════════════════════════════════════════════════════════════
        
        saveSettingsToSession(local);
        
        // Update parent state
        setSettings(local);
        setHasChanges(false);
        
        setToast({ message: "Settings saved to browser session", type: "success" });
        
      } else {
        // ═══════════════════════════════════════════════════════════════════
        // PAID TIER - Save to database
        // ═══════════════════════════════════════════════════════════════════
        
        const updatedSettings = await updateSettings(local, brand);
        
        // Update parent state (cache)
        setSettings(updatedSettings);
        setLocal(updatedSettings);
        setHasChanges(false);
        
        setToast({ message: "Settings saved successfully!", type: "success" });
      }
      
    } catch (error) {
      console.error("Failed to save settings:", error);
      
      if (error.name === 'PermissionDeniedError') {
        setToast({ message: "Upgrade required for this feature", type: "error" });
      } else {
        setToast({ message: "Failed to save settings. Please try again.", type: "error" });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setLocal(settings);
    setHasChanges(false);
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // DELETE CUSTOM FIELD HANDLER - TIER-BASED
  // ══════════════════════════════════════════════════════════════════════════════

  const handleDeleteField = async (fieldNum) => {
    if (isFreeTier) {
      // FREE tier - just disable field locally (no DB operation)
      setLocal(p => ({
        ...p,
        [`customField${fieldNum}`]: { 
          enabled: false, 
          name: "", 
          showInInvoice: true 
        }
      }));
      setToast({ message: "Custom field disabled", type: "success" });
      return;
    }

    const field = local[`customField${fieldNum}`];
    const fieldName = field?.name || `Custom Field ${fieldNum}`;
    const fieldKey = `customField${fieldNum}Value`;
    
    const confirmed = window.confirm(
      `⚠️ Delete "${fieldName}" and all its data?\n\n` +
      `This will permanently remove ${fieldName} values from ALL products in your catalogue.\n\n` +
      `This action cannot be undone.\n\n` +
      `Click OK to delete, or Cancel to keep the field.`
    );
    
    if (!confirmed) return;
    
    setSaving(true);
    
    try {
      // Clear field data from all products
      const count = await clearCustomFieldFromAllProducts(fieldKey);
      
      // Disable field in settings
      setLocal(p => ({
        ...p,
        [`customField${fieldNum}`]: { 
          enabled: false, 
          name: "", 
          showInInvoice: true 
        }
      }));
      
      setToast({ message: `Deleted ${fieldName} from ${count} products`, type: "success" });
      
    } catch (error) {
      console.error("Failed to delete custom field:", error);
      setToast({ message: "Failed to delete custom field. Please try again.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // DATA DELETION HANDLERS - TIER-BASED (DISABLED FOR FREE)
  // ══════════════════════════════════════════════════════════════════════════════

  const handleDeleteInvoices = async () => {
    if (isFreeTier) return; // Disabled for FREE tier
    
    if (!window.confirm(
      "⚠️ Delete All Invoice Data?\n\n" +
      "This will hide all invoices from history and reports.\n\n" +
      "✓ 30-day recovery period available\n" +
      "✓ You can restore them anytime within 30 days\n" +
      "✓ After 30 days, data will be permanently deleted\n\n" +
      "Continue?"
    )) {
      return;
    }

    setSaving(true);
    try {
      const result = await deleteAllInvoices();
      setToast({ 
        message: `${result.invoiceCount} invoices deleted. Recovery available for 30 days.`, 
        type: "success" 
      });
    } catch (error) {
      console.error("Failed to delete invoices:", error);
      setToast({ message: "Failed to delete invoices. Please try again.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProducts = async () => {
    if (isFreeTier) return; // Disabled for FREE tier
    
    if (!window.confirm(
      "⚠️ Delete All Product Data?\n\n" +
      "This will hide all products from your catalogue.\n\n" +
      "✓ 30-day recovery period available\n" +
      "✓ You can restore them anytime within 30 days\n" +
      "✓ After 30 days, data will be permanently deleted\n\n" +
      "Continue?"
    )) {
      return;
    }

    setSaving(true);
    try {
      const result = await deleteAllProducts();
      setToast({ 
        message: `${result.productCount} products deleted. Recovery available for 30 days.`, 
        type: "success" 
      });
    } catch (error) {
      console.error("Failed to delete products:", error);
      setToast({ message: "Failed to delete products. Please try again.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomers = async () => {
    if (isFreeTier) return; // Disabled for FREE tier
    
    if (!window.confirm(
      "⚠️ Delete All Customer Data?\n\n" +
      "This will hide all customers from your records.\n\n" +
      "✓ 30-day recovery period available\n" +
      "✓ You can restore them anytime within 30 days\n" +
      "✓ After 30 days, data will be permanently deleted\n\n" +
      "Continue?"
    )) {
      return;
    }

    setSaving(true);
    try {
      const result = await deleteAllCustomers();
      setToast({ 
        message: `${result.customerCount} customers deleted. Recovery available for 30 days.`, 
        type: "success" 
      });
    } catch (error) {
      console.error("Failed to delete customers:", error);
      setToast({ message: "Failed to delete customers. Please try again.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = async () => {
    if (!window.confirm(
      "⚠️ Reset All Settings to Default?\n\n" +
      "This will restore all settings to factory defaults.\n\n" +
      "Continue?"
    )) {
      return;
    }

    setSaving(true);
    try {
      if (isFreeTier) {
        // ═══════════════════════════════════════════════════════════════════
        // FREE TIER - Reset sessionStorage
        // ═══════════════════════════════════════════════════════════════════
        
        saveSettingsToSession(DEFAULT_SETTINGS);
        setSettings(DEFAULT_SETTINGS);
        setLocal(DEFAULT_SETTINGS);
        setHasChanges(false);
        
        setToast({ message: "Settings reset to defaults", type: "success" });
        
      } else {
        // ═══════════════════════════════════════════════════════════════════
        // PAID TIER - Reset database
        // ═══════════════════════════════════════════════════════════════════
        
        const updatedSettings = await updateSettings(DEFAULT_SETTINGS, brand);
        setSettings(updatedSettings);
        setLocal(updatedSettings);
        setHasChanges(false);
        
        setToast({ message: "Settings reset to defaults", type: "success" });
      }
    } catch (error) {
      console.error("Failed to reset settings:", error);
      setToast({ message: "Failed to reset settings. Please try again.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // BATCH RESTORATION HANDLERS - INVOICES (DISABLED FOR FREE)
  // ══════════════════════════════════════════════════════════════════════════════

  const handleShowInvoiceBatches = async () => {
    if (isFreeTier) return; // Disabled for FREE tier
    
    setLoadingInvoiceBatches(true);
    setShowInvoiceBatchModal(true);
    
    try {
      const batches = await getDeletionBatches();
      setInvoiceBatches(batches);
    } catch (error) {
      console.error("Failed to load invoice deletion batches:", error);
      setToast({ message: "Failed to load invoice deletion batches", type: "error" });
    } finally {
      setLoadingInvoiceBatches(false);
    }
  };

  const handleRestoreInvoiceBatch = async (batchId) => {
    if (isFreeTier) return; // Disabled for FREE tier
    
    if (!window.confirm("Restore this batch of invoices?")) return;

    try {
      const result = await restoreInvoiceBatch(batchId);
      setToast({ message: `${result.invoiceCount} invoices restored`, type: "success" });
      
      // Refresh batch list
      const batches = await getDeletionBatches();
      setInvoiceBatches(batches);
    } catch (error) {
      console.error("Failed to restore invoice batch:", error);
      setToast({ message: "Failed to restore batch", type: "error" });
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // BATCH RESTORATION HANDLERS - PRODUCTS (DISABLED FOR FREE)
  // ══════════════════════════════════════════════════════════════════════════════

  const handleShowProductBatches = async () => {
    if (isFreeTier) return; // Disabled for FREE tier
    
    setLoadingProductBatches(true);
    setShowProductBatchModal(true);
    
    try {
      const batches = await getProductDeletionBatches();
      setProductBatches(batches);
    } catch (error) {
      console.error("Failed to load product deletion batches:", error);
      setToast({ message: "Failed to load product deletion batches", type: "error" });
    } finally {
      setLoadingProductBatches(false);
    }
  };

  const handleRestoreProductBatch = async (batchId) => {
    if (isFreeTier) return; // Disabled for FREE tier
    
    if (!window.confirm("Restore this batch of products?")) return;

    try {
      const result = await restoreProductBatch(batchId);
      setToast({ message: `${result.productCount} products restored`, type: "success" });
      
      // Refresh batch list
      const batches = await getProductDeletionBatches();
      setProductBatches(batches);
    } catch (error) {
      console.error("Failed to restore product batch:", error);
      setToast({ message: "Failed to restore batch", type: "error" });
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // BATCH RESTORATION HANDLERS - CUSTOMERS (DISABLED FOR FREE)
  // ══════════════════════════════════════════════════════════════════════════════

  const handleShowCustomerBatches = async () => {
    if (isFreeTier) return; // Disabled for FREE tier
    
    setLoadingCustomerBatches(true);
    setShowCustomerBatchModal(true);
    
    try {
      const batches = await getCustomerDeletionBatches();
      setCustomerBatches(batches);
    } catch (error) {
      console.error("Failed to load customer deletion batches:", error);
      setToast({ message: "Failed to load customer deletion batches", type: "error" });
    } finally {
      setLoadingCustomerBatches(false);
    }
  };

  const handleRestoreCustomerBatch = async (batchId) => {
    if (isFreeTier) return; // Disabled for FREE tier
    
    if (!window.confirm("Restore this batch of customers?")) return;

    try {
      const result = await restoreCustomerBatch(batchId);
      setToast({ message: `${result.customerCount} customers restored`, type: "success" });
      
      // Refresh batch list
      const batches = await getCustomerDeletionBatches();
      setCustomerBatches(batches);
    } catch (error) {
      console.error("Failed to restore customer batch:", error);
      setToast({ message: "Failed to restore batch", type: "error" });
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // RESET BRAND HANDLER - TIER-BASED
  // ══════════════════════════════════════════════════════════════════════════════

  const handleResetBrand = async () => {
    if (!window.confirm(
      "⚠️ Reset Brand Information to Default?\n\n" +
      "This will clear all business details, address, tax info, and uploaded images.\n\n" +
      "Continue?"
    )) {
      return;
    }

    setSaving(true);
    try {
      if (isFreeTier) {
        // ═══════════════════════════════════════════════════════════════════
        // FREE TIER - Reset sessionStorage
        // ═══════════════════════════════════════════════════════════════════
        
        saveBrandToSession(DEFAULT_BRAND);
        setBrand(DEFAULT_BRAND);
        
        setToast({ message: "Brand info reset to defaults", type: "success" });
        
      } else {
        // ═══════════════════════════════════════════════════════════════════
        // PAID TIER - Reset database
        // ═══════════════════════════════════════════════════════════════════
        
        await updateBrandInfo({});
        
        // Fetch fresh brand info
        const freshBrand = await getCurrentBrandInfo();
        
        // Update parent state
        setBrand(freshBrand || DEFAULT_BRAND);
        
        setToast({ message: "Brand info reset to defaults", type: "success" });
      }
    } catch (error) {
      console.error("Failed to reset brand:", error);
      setToast({ message: "Failed to reset brand info", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Check if user has feature access
  const hasFeature = (feature) => user?.features?.[feature] === true;

  return (
    <div style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Invoice Batch Restoration Modal */}
      {showInvoiceBatchModal && (
        <BatchRestorationModal
          G={G}
          entityType="invoice"
          batches={invoiceBatches}
          loading={loadingInvoiceBatches}
          onRestore={handleRestoreInvoiceBatch}
          onClose={() => setShowInvoiceBatchModal(false)}
        />
      )}

      {/* Product Batch Restoration Modal */}
      {showProductBatchModal && (
        <BatchRestorationModal
          G={G}
          entityType="product"
          batches={productBatches}
          loading={loadingProductBatches}
          onRestore={handleRestoreProductBatch}
          onClose={() => setShowProductBatchModal(false)}
        />
      )}

      {/* Customer Batch Restoration Modal */}
      {showCustomerBatchModal && (
        <BatchRestorationModal
          G={G}
          entityType="customer"
          batches={customerBatches}
          loading={loadingCustomerBatches}
          onRestore={handleRestoreCustomerBatch}
          onClose={() => setShowCustomerBatchModal(false)}
        />
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          <h2 style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 600, color: G.ink, marginBottom: 4 }}>
            ⚙️ Settings
          </h2>
          <div style={{ color: G.muted, fontSize: 13 }}>
            {hasChanges ? (
              <span style={{ color: G.accent, fontWeight: 500 }}>● Unsaved changes</span>
            ) : (
              "Configure your application preferences"
            )}
          </div>
        </div>
      </div>

      {/* 1. Appearance */}
      <SettingCard G={G}>
        <SectionHeader 
          G={G}
          icon="🎨"
          title="Appearance" 
        />
        <ModernToggle 
          G={G}
          label="Dark Mode" 
          value={darkMode} 
          onChange={setDarkMode}
          description="Switch between light and dark interface themes"
        />
      </SettingCard>

      {/* 2. Date & Time Formatting */}
      <SettingCard G={G}>
        <SectionHeader 
          G={G}
          icon="📅"
          title="Date & Time Formatting" 
          description="Choose how dates and times appear on invoices"
        />
        
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 20}}>
          <div style={{ paddingRight: 20, borderRight: `2px solid ${G.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Time Format
            </div>
            <ButtonGroup 
              G={G}
              options={[
                ["12hr", "12-Hour (2:30 PM)"],
                ["24hr", "24-Hour (14:30)"]
              ]}
              value={local.timeFormat || "12hr"}
              onChange={val => setLocal(p => ({ ...p, timeFormat: val }))}
              columns="1fr"
            />
          </div>

          <div style={{ paddingLeft: 20, paddingRight: 20, borderRight: `2px solid ${G.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Date Order
            </div>
            <ButtonGroup 
              G={G}
              options={[
                ["DD-MM-YYYY", "DD-MM-YYYY"],
                ["MM-DD-YYYY", "MM-DD-YYYY"],
                ["YYYY-MM-DD", "YYYY-MM-DD"]
              ]}
              value={local.dateFormat || "DD-MM-YYYY"}
              onChange={val => setLocal(p => ({ ...p, dateFormat: val }))}
              columns="1fr"
            />
          </div>

          <div style={{ paddingLeft: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Date Separator
            </div>
            <ButtonGroup 
              G={G}
              options={[
                ["-", "Hyphen (15-01-2024)"],
                ["/", "Slash (15/01/2024)"]
              ]}
              value={local.dateSeparator || "-"}
              onChange={val => setLocal(p => ({ ...p, dateSeparator: val }))}
              columns="1fr"
            />
          </div>
        </div>

        <PreviewBox G={G} label="Preview" variant="accent">
          {(() => {
            const now = new Date();
            const timeFormat = local.timeFormat || "12hr";
            
            if (timeFormat === "12hr") {
              return now.toLocaleString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
              });
            } else {
              return now.toLocaleString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false
              });
            }
          })()}
        </PreviewBox>
      </SettingCard>

      {/* 3. Currency & Amount Formatting */}
      <SettingCard G={G}>
        <SectionHeader 
          G={G}
          icon="💰"
          title="Currency & Amount Formatting" 
          description="Configure how currency and numbers are displayed"
        />

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: G.ink }}>
            Currency Display Format
          </div>
          <div style={{ position: "relative" }}>
            <select 
              value={local.currencyFormat || "symbol-before-space"} 
              onChange={e => setLocal(p => ({ ...p, currencyFormat: e.target.value }))}
              style={{
                width: "100%",
                padding: "10px 12px",
                paddingRight: "36px",
                border: `1.5px solid ${G.border}`,
                borderRadius: 7,
                background: G.surface,
                color: G.ink,
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                cursor: "pointer",
                appearance: "none"
              }}
            >
              {CURRENCY_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <span style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              color: G.muted,
              fontSize: 14
            }}>
              ▼
            </span>
          </div>
          
          {(local.currencyFormat || "symbol-before-space").includes("before") && (
  <div style={{
    background: darkMode ? "rgba(239, 68, 68, 0.15)" : "#FEF2F2",
    border: darkMode ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 12,
    color: darkMode ? "#FCA5A5" : "#991B1B",
    marginTop: 8,
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    lineHeight: 1.4
  }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
              <span>
Notice: Some languages which read from right-to-left (e.g. Arabic, Persian, Hebrew, Urdu) may not render currency display format correctly with "symbol/code before amount" formats. For accurate display, use "symbol after" or "no symbol" option from settings.</span>
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 20 }}>
          <div style={{ paddingRight: 20, borderRight: `2px solid ${G.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Number Grouping Style
            </div>
            <ButtonGroup 
              G={G}
              options={[
                ["international", "International (1,234,567)"],
                ["indian", "Indian (12,34,567)"]
              ]}
              value={local.amountGrouping || "international"}
              onChange={val => setLocal(p => ({ ...p, amountGrouping: val }))}
              columns="1fr"
            />
          </div>

          <div style={{ paddingLeft: 20, paddingRight: 20, borderRight: `2px solid ${G.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Separator Style
            </div>
            <ButtonGroup 
              G={G}
              options={[
                ["comma-dot", "Comma, Dot (1,234.56)"],
                ["dot-comma", "Dot, Comma (1.234,56)"]
              ]}
              value={local.amountSeparator || "comma-dot"}
              onChange={val => setLocal(p => ({ ...p, amountSeparator: val }))}
              columns="1fr"
            />
          </div>

          <div style={{ paddingLeft: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Decimal Places
            </div>
            <ButtonGroup 
              G={G}
              options={[
                [true, "Show Decimals (1,234.56)"],
                [false, "Hide Decimals (1,234)"]
              ]}
              value={local.amountDecimals !== false}
              onChange={val => setLocal(p => ({ ...p, amountDecimals: val }))}
              columns="1fr"
            />
          </div>
        </div>

        <PreviewBox G={G} label="Preview" variant="accent">
          {(() => {
            const testAmount = 1234567.89;
            const grouping = local.amountGrouping || "international";
            const separator = local.amountSeparator || "comma-dot";
            const decimals = local.amountDecimals !== false;
            
            let formatted = "";
            
            if (grouping === "indian") {
              if (separator === "comma-dot") {
                formatted = testAmount.toLocaleString("en-IN", {
                  minimumFractionDigits: decimals ? 2 : 0,
                  maximumFractionDigits: decimals ? 2 : 0
                });
              } else {
                const parts = testAmount.toFixed(decimals ? 2 : 0).split(".");
                const intPart = parts[0];
                const decPart = parts[1];
                const reversed = intPart.split("").reverse().join("");
                let grouped = "";
                for (let i = 0; i < reversed.length; i++) {
                  if (i === 3 || (i > 3 && (i - 3) % 2 === 0)) grouped = "." + grouped;
                  grouped = reversed[i] + grouped;
                }
                formatted = decimals ? `${grouped},${decPart}` : grouped;
              }
            } else {
              if (separator === "comma-dot") {
                formatted = testAmount.toLocaleString("en-US", {
                  minimumFractionDigits: decimals ? 2 : 0,
                  maximumFractionDigits: decimals ? 2 : 0
                });
              } else {
                formatted = testAmount.toLocaleString("de-DE", {
                  minimumFractionDigits: decimals ? 2 : 0,
                  maximumFractionDigits: decimals ? 2 : 0
                });
              }
            }
            
            return `$ ${formatted}`;
          })()}
        </PreviewBox>
      </SettingCard>

      {/* 4. Invoice Numbering */}
      <SettingCard G={G}>
        <SectionHeader 
          G={G}
          icon="🔢"
          title="Invoice Numbering" 
          description="Customize invoice number format and counter behavior"
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 20 }}>
          <div style={{ paddingRight: 20, borderRight: `2px solid ${G.border}` }}>
            <label style={{ 
              fontSize: 11, 
              fontWeight: 600, 
              color: G.muted, 
              letterSpacing: .6, 
              textTransform: "uppercase", 
              display: "block", 
              marginBottom: 6 
            }}>
              Prefix {!hasFeature(FEATURES.CUSTOM_INVOICE_NUMBERING) && (
                <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.7 }}>(Premium)</span>
              )}
            </label>
            <input
              value={local.invPrefix || ""}
              onChange={e => {
                if (!hasFeature(FEATURES.CUSTOM_INVOICE_NUMBERING)) return;
                const cleaned = e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 15);
                setLocal(p => ({ ...p, invPrefix: cleaned }));
              }}
              placeholder="e.g. INV, SALES"
              maxLength={15}
              disabled={!hasFeature(FEATURES.CUSTOM_INVOICE_NUMBERING)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1.5px solid ${G.border}`,
                borderRadius: 7,
                background: G.surface,
                color: G.ink,
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                textAlign: "center",
                opacity: hasFeature(FEATURES.CUSTOM_INVOICE_NUMBERING) ? 1 : 0.5,
                cursor: hasFeature(FEATURES.CUSTOM_INVOICE_NUMBERING) ? "text" : "not-allowed"
              }}
            />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 4, textAlign: "left" }}>
              {(local.invPrefix || "").length}/15 characters
            </div>
          </div>

          <div style={{ paddingLeft: 20, paddingRight: 20, borderRight: `2px solid ${G.border}` }}>
            <label style={{ 
              fontSize: 11, 
              fontWeight: 600, 
              color: G.muted, 
              letterSpacing: .6, 
              textTransform: "uppercase", 
              display: "block", 
              marginBottom: 6 
            }}>
              Date Format
            </label>
            <div style={{ position: "relative" }}>
              <select 
                value={local.invDateFormat || "YYYY-MM-DD"} 
                onChange={e => setLocal(p => ({ ...p, invDateFormat: e.target.value }))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  paddingRight: "36px",
                  border: `1.5px solid ${G.border}`,
                  borderRadius: 7,
                  background: G.surface,
                  color: G.ink,
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 13,
                  cursor: "pointer",
                  textAlign: "center",
                  appearance: "none"
                }}
              >
                <option value="NONE">No Date</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                <option value="MM-DD-YYYY">MM-DD-YYYY</option>
                <option value="YYYY-MM">YYYY-MM</option>
                <option value="MM-YYYY">MM-YYYY</option>
                <option value="YYYY">YYYY</option>
              </select>
              <span style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: G.muted,
                fontSize: 14
              }}>
                ▼
              </span>
            </div>
            <div style={{ fontSize: 10, color: G.muted, marginTop: 4, textAlign: "left" }}>
              {(() => {
                const format = local.invDateFormat || "YYYY-MM-DD";
                if (format === "NONE") return "Counter never resets";
                if (format.includes("DD")) return "Resets daily";
                if (format.includes("MM")) return "Resets monthly";
                if (format === "YYYY") return "Resets yearly";
                return "";
              })()}
            </div>
          </div>

          <div style={{ paddingLeft: 20 }}>
            <label style={{ 
              fontSize: 11, 
              fontWeight: 600, 
              color: G.muted, 
              letterSpacing: .6, 
              textTransform: "uppercase", 
              display: "block", 
              marginBottom: 6 
            }}>
              Counter Digits
            </label>
            <div style={{ position: "relative" }}>
              <select
                value={local.invMinDigits || 4}
                onChange={e => setLocal(p => ({ ...p, invMinDigits: parseInt(e.target.value) }))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  paddingRight: "36px",
                  border: `1.5px solid ${G.border}`,
                  borderRadius: 7,
                  background: G.surface,
                  color: G.ink,
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 13,
                  cursor: "pointer",
                  textAlign: "center",
                  appearance: "none"
                }}
              >
                {[2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>{n} digits</option>
                ))}
              </select>
              <span style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: G.muted,
                fontSize: 14
              }}>
                ▼
              </span>
            </div>
            <div style={{ fontSize: 10, color: G.muted, marginTop: 4, textAlign: "left" }}>
              Max {Math.pow(10, local.invMinDigits || 4) - 1} invoices
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ 
            fontSize: 11, 
            fontWeight: 600, 
            color: G.muted, 
            letterSpacing: .6, 
            textTransform: "uppercase", 
            display: "block", 
            marginBottom: 6 
          }}>
            Separator
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            {["-", "_"].map(sep => (
              <button
                key={sep}
                onClick={() => setLocal(p => ({ ...p, invSeparator: sep }))}
                style={{
                  padding: "10px 24px",
                  border: `1.5px solid ${(local.invSeparator || "-") === sep ? G.accent : G.border}`,
                  borderRadius: 7,
                  background: (local.invSeparator || "-") === sep ? G.accentBg : G.surface,
                  color: (local.invSeparator || "-") === sep ? G.accent : G.ink2,
                  fontSize: 13,
                  fontWeight: (local.invSeparator || "-") === sep ? 600 : 400,
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  transition: "all .15s"
                }}
              >
                {sep === "-" ? "Hyphen (-)" : "Underscore (_)"}
              </button>
            ))}
          </div>
        </div>

        {(() => {
          const format = local.invDateFormat || "YYYY-MM-DD";
          const digits = local.invMinDigits || 4;
          const maxInvoices = Math.pow(10, digits) - 1;

const infoBoxStyle = {
  background: darkMode ? "rgba(234, 179, 8, 0.15)" : "#FFFBEB",
  border: darkMode ? "1.8px solid rgba(234, 179, 8, 0.3)" : "1.8px solid rgba(234, 179, 8, 0.35)",
  borderRadius: 8,
  padding: "12px 14px",
  fontSize: 12,
  color: darkMode ? "#FCD34D" : "#A16207",
  marginBottom: 16,
  lineHeight: 1.6
};

          if (format === "NONE") {
            return (
              <div style={infoBoxStyle}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  ℹ️ Pure Sequential Mode
                </div>
                <div>
                  Counter <strong>never resets</strong> and increases continuously: 1, 2, 3, 4...
                  <br/>
                  With <strong>{digits} digits</strong>, you can generate up to <strong>{maxInvoices.toLocaleString()} unique invoices</strong>.
                  <br/>
                  After reaching {maxInvoices.toLocaleString()}, the counter will <strong>auto-expand to {digits + 1} digits</strong> to maintain uniqueness.
                </div>
              </div>
            );
          }

          if (format.includes("DD")) {
            return (
              <div style={infoBoxStyle}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  ℹ️ Daily Reset Mode
                </div>
                <div>
                  Counter resets to 1 every day at midnight.
                  <br/>
                  With {digits} digits, you can generate up to <strong>{maxInvoices.toLocaleString()} invoices per day</strong>.
                </div>
              </div>
            );
          }

          if (format.includes("MM")) {
            return (
              <div style={infoBoxStyle}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  ℹ️ Monthly Reset Mode
                </div>
                <div>
                  Counter resets to 1 on the 1st of each month.
                  <br/>
                  With {digits} digits, you can generate up to <strong>{maxInvoices.toLocaleString()} invoices per month</strong>.
                </div>
              </div>
            );
          }

          if (format === "YYYY") {
            return (
              <div style={infoBoxStyle}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  ℹ️ Yearly Reset Mode
                </div>
                <div>
                  Counter resets to 1 on January 1st each year.
                  <br/>
                  With {digits} digits, you can generate up to <strong>{maxInvoices.toLocaleString()} invoices per year</strong>.
                </div>
              </div>
            );
          }
        })()}

        <PreviewBox G={G} label="Live Preview — Next Invoice" variant="accent">
          {(() => {
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, "0");
            const dd = String(today.getDate()).padStart(2, "0");
            
            const prefix = local.invPrefix || "INV";
            const separator = local.invSeparator || "-";
            const digits = local.invMinDigits || 4;
            const counter = "1".padStart(digits, "0");
            const format = local.invDateFormat || "YYYY-MM-DD";
            
            let datePart = "";
            switch (format) {
              case "NONE": datePart = ""; break;
              case "YYYY-MM-DD": datePart = `${yyyy}${separator}${mm}${separator}${dd}`; break;
              case "DD-MM-YYYY": datePart = `${dd}${separator}${mm}${separator}${yyyy}`; break;
              case "MM-DD-YYYY": datePart = `${mm}${separator}${dd}${separator}${yyyy}`; break;
              case "YYYY-MM": datePart = `${yyyy}${separator}${mm}`; break;
              case "MM-YYYY": datePart = `${mm}${separator}${yyyy}`; break;
              case "YYYY": datePart = `${yyyy}`; break;
              default: datePart = `${yyyy}${separator}${mm}${separator}${dd}`;
            }
            
            if (datePart) {
              return `${prefix}${separator}${datePart}${separator}${counter}`;
            } else {
              return prefix ? `${prefix}${separator}${counter}` : counter;
            }
          })()}
        </PreviewBox>
      </SettingCard>

      {/* 5. Page Setup */}
      <SettingCard G={G}>
        <SectionHeader 
          G={G}
          icon="📄"
          title="Page Setup" 
          description="Choose the paper size for printing and PDF download"
        />
        <div>
          <label style={{ 
            fontSize: 11, 
            fontWeight: 600, 
            color: G.muted, 
            letterSpacing: .6, 
            textTransform: "uppercase", 
            display: "block", 
            marginBottom: 6 
          }}>
            Page Size
          </label>
          <div style={{ position: "relative" }}>
            <select
              value={local.pageSize || "A4"}
              onChange={e => setLocal(p => ({ ...p, pageSize: e.target.value }))}
              style={{
                width: "100%",
                padding: "10px 12px",
                paddingRight: "36px",
                border: `1.5px solid ${G.border}`,
                borderRadius: 7,
                background: G.surface,
                color: G.ink,
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                cursor: "pointer",
                appearance: "none"
              }}
            >
              <option value="A4">A4 (210 × 297 mm)</option>
              <option value="LETTER">Letter (8.5 × 11 in)</option>
              <option value="LEGAL">Legal (8.5 × 14 in)</option>
              <option value="A5">A5 (148 × 210 mm)</option>
              <option value="STATEMENT">Statement (5.5 × 8.5 in)</option>
            </select>
            <span style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              color: G.muted,
              fontSize: 14
            }}>
              ▼
            </span>
          </div>
        </div>
      </SettingCard>

      {/* 7. Invoice Columns */}
      <SettingCard G={G}>
        <SectionHeader 
          G={G}
          icon="📋"
          title="Invoice Columns" 
          description="Toggle which columns appear on printed/PDF invoices"
        />
        
        <ModernToggle 
          G={G}
          label="SKU, Product ID / Code" 
          value={local.showProductID || false} 
          onChange={v => setLocal(p => ({ ...p, showProductID: v }))}
        />
        
        <ModernToggle 
          G={G}
          label="Commodity / CN / HSN Code" 
          value={local.showHSN || false} 
          onChange={v => setLocal(p => ({ ...p, showHSN: v }))}
        />
        
        {local.customField1?.enabled && (
          <ModernToggle 
            G={G}
            label="Custom Field 1"
            value={local.customField1.showInInvoice !== false} 
            onChange={v => setLocal(p => ({ 
              ...p, 
              customField1: { ...p.customField1, showInInvoice: v } 
            }))}
          />
        )}
        
        {local.customField2?.enabled && (
          <ModernToggle 
            G={G}
            label="Custom Field 2"
            value={local.customField2.showInInvoice !== false} 
            onChange={v => setLocal(p => ({ 
              ...p, 
              customField2: { ...p.customField2, showInInvoice: v } 
            }))}
          />
        )}
      </SettingCard>

      {/* 8. Custom Product Fields */}
      <SettingCard G={G}>
        <SectionHeader 
          G={G}
          icon="✏️"
          title="Custom Product Fields" 
          description="Add up to 2 custom fields for products (e.g. Batch No, Color, Size, Serial)"
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {[1, 2].map(num => {
            const field = local[`customField${num}`];
            const hasAccess = hasFeature(FEATURES.CUSTOM_FIELDS);
            
            return (
              <div key={num} style={{ 
                background: G.surface2, 
                borderRadius: 8, 
                padding: 16,
                border: `1px solid ${G.border}15`,
                opacity: hasAccess ? 1 : 0.5
              }}>
                <ModernToggle
                  G={G}
                  label={`Enable Custom Field ${num}${!hasAccess ? " (Premium)" : ""}`}
                  value={field?.enabled || false}
                  disabled={!hasAccess}
                  onChange={v => {
                    if (!hasAccess) return;
                    setLocal(p => ({ 
                      ...p, 
                      [`customField${num}`]: { 
                        ...(p[`customField${num}`] || {}), 
                        enabled: v,
                        showInInvoice: v
                      } 
                    }));
                  }}
                />
                {field?.enabled && hasAccess && (
                  <>
                    <div style={{ marginTop: 12 }}>
                      <label style={{ 
                        fontSize: 11, 
                        fontWeight: 600, 
                        color: G.muted, 
                        letterSpacing: .6, 
                        textTransform: "uppercase", 
                        display: "block", 
                        marginBottom: 6 
                      }}>
                        Field Name
                      </label>
                      <input
                        value={field.name || ""}
                        onChange={e => setLocal(p => ({
                          ...p,
                          [`customField${num}`]: { 
                            ...(p[`customField${num}`] || {}), 
                            name: e.target.value.slice(0, 12) 
                          }
                        }))}
                        placeholder={`e.g. Batch${num}, Color`}
                        maxLength={12}
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          border: `1.5px solid ${G.border}`,
                          borderRadius: 7,
                          background: G.surface,
                          color: G.ink,
                          fontFamily: "'Sora', sans-serif",
                          fontSize: 13
                        }}
                      />
                      <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                        {(field.name || "").length}/12 characters
                      </div>
                    </div>

                    {/* Delete Field Button */}
                    <button
                      onClick={() => handleDeleteField(num)}
                      disabled={saving || isFreeTier}
                      style={{
                        width: "100%",
                        marginTop: 12,
                        padding: "8px 12px",
                        background: isFreeTier ? G.surface2 : "#FEF2F2",
                        color: isFreeTier ? G.muted : "#DC2626",
                        border: `1px solid ${isFreeTier ? G.border : "rgba(239, 68, 68, 0.3)"}`,
                        borderRadius: 7,
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: (saving || isFreeTier) ? "not-allowed" : "pointer",
                        fontFamily: "'Sora', sans-serif",
                        transition: "all .15s",
                        opacity: (saving || isFreeTier) ? 0.5 : 1,
                        filter: isFreeTier ? "grayscale(1)" : "none"
                      }}
                      onMouseEnter={e => {
                        if (!saving && !isFreeTier) {
                          e.target.style.background = "#DC2626";
                          e.target.style.color = "#FFFFFF";
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isFreeTier) {
                          e.target.style.background = "#FEF2F2";
                          e.target.style.color = "#DC2626";
                        }
                      }}
                    >
                      🗑️ Delete Field & All Data
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </SettingCard>

      {/* 9. Notes & Terms */}
      <SettingCard G={G}>
        <SectionHeader 
  G={G}
  icon="📝"
  title="Invoice Notes & Terms" 
  description="Printed at the bottom of every invoice • Max 3 sections • Title: 60 chars • Body: 1000 chars"
/>

<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
  <div style={{ fontSize: 12, color: G.muted }}>
    {(local.notesSections || []).length} of 3 sections used
  </div>
  <button 
    onClick={addSection}
    disabled={(local.notesSections || []).length >= 3}
    style={{
      padding: "8px 14px",
      border: "none",
      borderRadius: 7,
      background: (local.notesSections || []).length >= 3 ? G.border : G.accent,
      color: (local.notesSections || []).length >= 3 ? G.muted : G.accentFg,
      fontSize: 12,
      fontWeight: 600,
      cursor: (local.notesSections || []).length >= 3 ? "not-allowed" : "pointer",
      fontFamily: "'Sora', sans-serif",
      opacity: (local.notesSections || []).length >= 3 ? 0.5 : 1
    }}
  >
    + Add Section
  </button>
</div>

{(local.notesSections || []).length === 0 && (
  <div style={{ 
    textAlign: "center", 
    padding: "40px 20px", 
    border: `2px dashed ${G.border}`, 
    borderRadius: 8, 
    color: G.muted,
    fontSize: 13
  }}>
    No sections yet — add up to 3 sections for terms, payment info, or policies
  </div>
)}

{(local.notesSections || []).map((s, i) => {
  const titleLength = (s.title || "").length;
  const bodyLength = (s.body || "").length;
  const titleAtLimit = titleLength >= 60;
  const bodyAtLimit = bodyLength >= 1000;
  
  return (
    <div key={i} style={{ 
      background: G.surface2, 
      borderRadius: 8, 
      padding: 14, 
      marginBottom: 10,
      border: `1px solid ${G.border}15`
    }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <input 
  value={s.title} 
  onChange={e => updateSection(i, "title", e.target.value.slice(0, 60))} 
  placeholder="Section title (e.g. Terms and Conditions, Return Policy)" 
  maxLength={60}
  style={{ 
    width: "100%",
    fontWeight: 600, 
    fontSize: 13,
    padding: "8px 10px",
    border: `1.5px solid ${G.border}`,
    borderRadius: 7,
    background: G.surface,
    color: G.ink,
    fontFamily: "'Sora', sans-serif"
  }} 
/>
<div style={{ 
  fontSize: 10, 
  color: G.muted, 
  marginTop: 3
}}>
  {titleLength}/60 characters
</div>
        </div>
        <button 
          onClick={() => removeSection(i)}
          style={{
            width: 32,
            height: 32,
            background: "#FEF2F2",
            color: "#EF4444",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            borderRadius: 7,
            fontSize: 16,
            cursor: "pointer",
            fontFamily: "sans-serif",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          ✕
        </button>
      </div>
      <textarea 
  rows={3} 
  value={s.body} 
  onChange={e => updateSection(i, "body", e.target.value.slice(0, 1000))} 
  placeholder="Write your terms here..."
  maxLength={1000}
  style={{
    width: "100%",
    padding: "8px 10px",
    border: `1.5px solid ${G.border}`,
    borderRadius: 7,
    background: G.surface,
    color: G.ink,
    fontFamily: "'Sora', sans-serif",
    fontSize: 12,
    resize: "vertical"
  }}
/>
<div style={{ 
  fontSize: 10, 
  color: G.muted, 
  marginTop: 3
}}>
  {bodyLength}/1000 characters
</div>
    </div>
  );
})}
      </SettingCard>

      {/* 10. Data Management */}
<SettingCard G={G}>
  <SectionHeader 
    G={G}
    icon="🗂️"
    title="Data Management" 
    description="Manage your business data — use with caution"
  />
  
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    {/* Delete Invoices + Restore */}
    <div style={{ 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center",
      padding: "12px 16px",
      background: G.surface2,
      borderRadius: 8,
      border: `1px solid ${G.border}15`
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: G.ink, marginBottom: 2 }}>
          🗑️ Invoice Data
        </div>
        <div style={{ fontSize: 11, color: G.muted }}>
          Delete all invoices with 30-day recovery
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={handleDeleteInvoices}
          disabled={saving || isFreeTier}
          style={{
            background: isFreeTier ? G.surface2 : "#FEF2F2",
            color: isFreeTier ? G.muted : "#F87171",
            border: isFreeTier ? `1px solid ${G.border}` : "1.8px solid rgba(248, 113, 113, 0.35)",
            borderRadius: 6,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: (saving || isFreeTier) ? "not-allowed" : "pointer",
            fontFamily: "'Sora', sans-serif",
            transition: "all .15s",
            opacity: isFreeTier ? 0.4 : 1,
            whiteSpace: "nowrap",
            filter: isFreeTier ? "grayscale(100%)" : "none"
          }}
          onMouseEnter={e => {
            if (!saving && !isFreeTier) {
              e.target.style.background = "#EF4444";
              e.target.style.color = "#FFFFFF";
              e.target.style.border = "1.8px solid transparent";
            }
          }}
          onMouseLeave={e => {
            if (!isFreeTier) {
              e.target.style.background = "#FEF2F2";
              e.target.style.color = "#F87171";
              e.target.style.border = "1.8px solid rgba(248, 113, 113, 0.35)";
            }
          }}
        >
          Delete
        </button>

        <button
          onClick={handleShowInvoiceBatches}
          disabled={saving || isFreeTier}
          style={{
            background: isFreeTier ? G.surface2 : G.accentBg,
            color: isFreeTier ? G.muted : G.accent,
            border: isFreeTier ? `1px solid ${G.border}` : `1.8px solid ${G.accent}40`,
            borderRadius: 6,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: (saving || isFreeTier) ? "not-allowed" : "pointer",
            fontFamily: "'Sora', sans-serif",
            transition: "all .15s",
            opacity: isFreeTier ? 0.4 : 1,
            whiteSpace: "nowrap",
            filter: isFreeTier ? "grayscale(100%)" : "none"
          }}
          onMouseEnter={e => {
            if (!saving && !isFreeTier) {
              e.target.style.background = G.accent;
              e.target.style.color = G.accentFg;
              e.target.style.border = "1.8px solid transparent";
            }
          }}
          onMouseLeave={e => {
            if (!isFreeTier) {
              e.target.style.background = G.accentBg;
              e.target.style.color = G.accent;
              e.target.style.border = `1.8px solid ${G.accent}40`;
            }
          }}
        >
          Restore
        </button>
      </div>
    </div>

    {/* Delete Products + Restore */}
    <div style={{ 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center",
      padding: "12px 16px",
      background: G.surface2,
      borderRadius: 8,
      border: `1px solid ${G.border}15`
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: G.ink, marginBottom: 2 }}>
          🗑️ Product Data
        </div>
        <div style={{ fontSize: 11, color: G.muted }}>
          Delete all products with 30-day recovery
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={handleDeleteProducts}
          disabled={saving || isFreeTier}
          style={{
            background: isFreeTier ? G.surface2 : "#FEF2F2",
            color: isFreeTier ? G.muted : "#F87171",
            border: isFreeTier ? `1px solid ${G.border}` : "1.8px solid rgba(248, 113, 113, 0.35)",
            borderRadius: 6,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: (saving || isFreeTier) ? "not-allowed" : "pointer",
            fontFamily: "'Sora', sans-serif",
            transition: "all .15s",
            opacity: isFreeTier ? 0.4 : 1,
            whiteSpace: "nowrap",
            filter: isFreeTier ? "grayscale(100%)" : "none"
          }}
          onMouseEnter={e => {
            if (!saving && !isFreeTier) {
              e.target.style.background = "#EF4444";
              e.target.style.color = "#FFFFFF";
              e.target.style.border = "1.8px solid transparent";
            }
          }}
          onMouseLeave={e => {
            if (!isFreeTier) {
              e.target.style.background = "#FEF2F2";
              e.target.style.color = "#F87171";
              e.target.style.border = "1.8px solid rgba(248, 113, 113, 0.35)";
            }
          }}
        >
          Delete
        </button>

        <button
          onClick={handleShowProductBatches}
          disabled={saving || isFreeTier}
          style={{
            background: isFreeTier ? G.surface2 : G.accentBg,
            color: isFreeTier ? G.muted : G.accent,
            border: isFreeTier ? `1px solid ${G.border}` : `1.8px solid ${G.accent}40`,
            borderRadius: 6,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: (saving || isFreeTier) ? "not-allowed" : "pointer",
            fontFamily: "'Sora', sans-serif",
            transition: "all .15s",
            opacity: isFreeTier ? 0.4 : 1,
            whiteSpace: "nowrap",
            filter: isFreeTier ? "grayscale(100%)" : "none"
          }}
          onMouseEnter={e => {
            if (!saving && !isFreeTier) {
              e.target.style.background = G.accent;
              e.target.style.color = G.accentFg;
              e.target.style.border = "1.8px solid transparent";
            }
          }}
          onMouseLeave={e => {
            if (!isFreeTier) {
              e.target.style.background = G.accentBg;
              e.target.style.color = G.accent;
              e.target.style.border = `1.8px solid ${G.accent}40`;
            }
          }}
        >
          Restore
        </button>
      </div>
    </div>

    {/* Delete Customers + Restore */}
    <div style={{ 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center",
      padding: "12px 16px",
      background: G.surface2,
      borderRadius: 8,
      border: `1px solid ${G.border}15`
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: G.ink, marginBottom: 2 }}>
          🗑️ Customer Data
        </div>
        <div style={{ fontSize: 11, color: G.muted }}>
          Delete all customers with 30-day recovery
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={handleDeleteCustomers}
          disabled={saving || isFreeTier}
          style={{
            background: isFreeTier ? G.surface2 : "#FEF2F2",
            color: isFreeTier ? G.muted : "#F87171",
            border: isFreeTier ? `1px solid ${G.border}` : "1.8px solid rgba(248, 113, 113, 0.35)",
            borderRadius: 6,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: (saving || isFreeTier) ? "not-allowed" : "pointer",
            fontFamily: "'Sora', sans-serif",
            transition: "all .15s",
            opacity: isFreeTier ? 0.4 : 1,
            whiteSpace: "nowrap",
            filter: isFreeTier ? "grayscale(100%)" : "none"
          }}
          onMouseEnter={e => {
            if (!saving && !isFreeTier) {
              e.target.style.background = "#EF4444";
              e.target.style.color = "#FFFFFF";
              e.target.style.border = "1.8px solid transparent";
            }
          }}
          onMouseLeave={e => {
            if (!isFreeTier) {
              e.target.style.background = "#FEF2F2";
              e.target.style.color = "#F87171";
              e.target.style.border = "1.8px solid rgba(248, 113, 113, 0.35)";
            }
          }}
        >
          Delete
        </button>

        <button
          onClick={handleShowCustomerBatches}
          disabled={saving || isFreeTier}
          style={{
            background: isFreeTier ? G.surface2 : G.accentBg,
            color: isFreeTier ? G.muted : G.accent,
            border: isFreeTier ? `1px solid ${G.border}` : `1.8px solid ${G.accent}40`,
            borderRadius: 6,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: (saving || isFreeTier) ? "not-allowed" : "pointer",
            fontFamily: "'Sora', sans-serif",
            transition: "all .15s",
            opacity: isFreeTier ? 0.4 : 1,
            whiteSpace: "nowrap",
            filter: isFreeTier ? "grayscale(100%)" : "none"
          }}
          onMouseEnter={e => {
            if (!saving && !isFreeTier) {
              e.target.style.background = G.accent;
              e.target.style.color = G.accentFg;
              e.target.style.border = "1.8px solid transparent";
            }
          }}
          onMouseLeave={e => {
            if (!isFreeTier) {
              e.target.style.background = G.accentBg;
              e.target.style.color = G.accent;
              e.target.style.border = `1.8px solid ${G.accent}40`;
            }
          }}
        >
          Restore
        </button>
      </div>
    </div>

    <div style={{ 
      height: 1, 
      background: G.border, 
      margin: "8px 0" 
    }} />

    {/* Reset Brand Info */}
    <div style={{ 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center",
      padding: "12px 16px",
      background: G.surface2,
      borderRadius: 8,
      border: `1px solid ${G.border}15`
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: G.ink, marginBottom: 2 }}>
          🏢 Brand Information
        </div>
        <div style={{ fontSize: 11, color: G.muted }}>
          Clear business details and uploaded images
        </div>
      </div>
      <button
        onClick={handleResetBrand}
        disabled={saving}
        style={{
          background: G.surface,
          color: G.ink,
          border: `1.5px solid ${G.border}`,
          borderRadius: 6,
          padding: "8px 16px",
          fontSize: 12,
          fontWeight: 600,
          cursor: saving ? "not-allowed" : "pointer",
          fontFamily: "'Sora', sans-serif",
          transition: "all .15s",
          opacity: saving ? 0.5 : 1,
          whiteSpace: "nowrap"
        }}
        onMouseEnter={e => {
          if (!saving) {
            e.target.style.background = G.ink;
            e.target.style.color = G.surface;
            e.target.style.border = "1.5px solid transparent";
          }
        }}
        onMouseLeave={e => {
          if (!saving) {
            e.target.style.background = G.surface;
            e.target.style.color = G.ink;
            e.target.style.border = `1.5px solid ${G.border}`;
          }
        }}
      >
        Reset
      </button>
    </div>

    {/* Reset Settings */}
    <div style={{ 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center",
      padding: "12px 16px",
      background: G.surface2,
      borderRadius: 8,
      border: `1px solid ${G.border}15`
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: G.ink, marginBottom: 2 }}>
          ⚙️ Application Settings
        </div>
        <div style={{ fontSize: 11, color: G.muted }}>
          Restore all settings to factory defaults
        </div>
      </div>
      <button
        onClick={handleResetSettings}
        disabled={saving}
        style={{
          background: G.surface,
          color: G.ink,
          border: `1.5px solid ${G.border}`,
          borderRadius: 6,
          padding: "8px 16px",
          fontSize: 12,
          fontWeight: 600,
          cursor: saving ? "not-allowed" : "pointer",
          fontFamily: "'Sora', sans-serif",
          transition: "all .15s",
          opacity: saving ? 0.5 : 1,
          whiteSpace: "nowrap"
        }}
        onMouseEnter={e => {
          if (!saving) {
            e.target.style.background = G.ink;
            e.target.style.color = G.surface;
            e.target.style.border = "1.5px solid transparent";
          }
        }}
        onMouseLeave={e => {
          if (!saving) {
            e.target.style.background = G.surface;
            e.target.style.color = G.ink;
            e.target.style.border = `1.5px solid ${G.border}`;
          }
        }}
      >
        Reset
      </button>
    </div>
  </div>
</SettingCard>

      {/* Bottom Save Button */}
      <div
        style={{
          position: "sticky",
          bottom: hasChanges ? 20 : 0,
          background: hasChanges ? G.accentBg : G.bg,
          border: hasChanges ? `1px solid ${G.accent}44` : `1px solid transparent`,
          borderRadius: 10,
          padding: hasChanges ? "14px 20px" : "8px 20px",
          marginTop: hasChanges ? 20 : 0,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: hasChanges ? 8 : 0,
          transition: "all .2s",
          boxShadow: hasChanges ? "0 -4px 12px rgba(0,0,0,0.1)" : "none",
        }}
      >
        {hasChanges && (
          <>
            <div
              style={{
                fontSize: 12,
                color: G.accent,
                fontWeight: 500,
                marginRight: "auto",
              }}
            >
              ● You have unsaved changes
            </div>
            <button
              onClick={handleDiscard}
              disabled={saving}
              style={{
                padding: "10px 24px",
                border: `1.8px solid ${G.border}`,
                borderRadius: 7,
                background: G.surface,
                color: G.ink,
                fontSize: 13,
                fontWeight: 600,
                cursor: saving ? "not-allowed" : "pointer",
                fontFamily: "'Sora', sans-serif",
                transition: "all .15s",
                opacity: saving ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.target.style.background = G.ink;
                  e.target.style.color = G.surface;
                  e.target.style.border = "1.8px solid transparent";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.background = G.surface;
                e.target.style.color = G.ink;
                e.target.style.border = `1.8px solid ${G.border}`;
              }}
            >
              Discard
            </button>
          </>
        )}

        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{
            padding: "10px 24px",
            border: hasChanges
              ? `1.8px solid ${G.accent}40`
              : `1.5px solid ${G.border}`,
            borderRadius: 7,
            background: hasChanges ? G.accentBg : G.surface2,
            color: hasChanges ? G.accent : G.muted,
            fontSize: 13,
            fontWeight: 600,
            cursor: hasChanges && !saving ? "pointer" : "not-allowed",
            fontFamily: "'Sora', sans-serif",
            transition: "all .15s",
          }}
          onMouseEnter={(e) => {
            if (hasChanges && !saving) {
              e.target.style.background = G.accent;
              e.target.style.color = G.accentFg;
              e.target.style.border = "1.8px solid transparent";
            }
          }}
          onMouseLeave={(e) => {
            if (hasChanges) {
              e.target.style.background = G.accentBg;
              e.target.style.color = G.accent;
              e.target.style.border = `1.8px solid ${G.accent}40`;
            }
          }}
        >
          {saving ? "Saving..." : (hasChanges ? "💾 Save All Settings" : "✓ All Saved")}
        </button>
      </div>
    </div>
  );
}